// iot-admin/client/src/pages/MomentsBillingPlans.js
// Super Admin — Moments Billing Plan CRUD + Tenant Assignment + Usage Cycles
// Enterprise-grade plan management for revenue experimentation.

import React, { useState, useEffect, useCallback } from 'react';
import { momentsPlanService, tenantService } from '../services/api';
import authClient from '../auth/authClient';
import {
  Package, Plus, Edit3, Archive, Users, RefreshCw,
  DollarSign, BarChart3, ChevronRight, X, Save,
  Tag, Calendar, TrendingUp, Shield, AlertTriangle,
  Layers, Zap, Globe, Search as SearchIcon,
  Sparkles, MessageSquare,
} from 'lucide-react';
import './MomentsBillingPlans.css';

/* ── helpers ── */
const fmt = (v) => Number(v || 0).toLocaleString('en-IN');
const fmtCurrency = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/* ── Tier labels ── */
const TIERS = [
  { key: 1, name: 'Context',    color: '#3b82f6', unit: 'call',       icon: '📍' },
  { key: 2, name: 'Conditions', color: '#10b981', unit: 'call',       icon: '⚡' },
  { key: 3, name: 'Audience',   color: '#8b5cf6', unit: 'query',      icon: '👥' },
  { key: 4, name: 'Site Eval',  color: '#f59e0b', unit: 'evaluation', icon: '🏢' },
];

/* ── Badge ── */
const Badge = ({ color, children }) => (
  <span className={`bp-badge bp-badge--${color}`}>{children}</span>
);

/* ===== PLAN CARD ===== */
const PlanCard = ({ plan, onEdit, onViewAssignments, onArchive }) => (
  <div className={`bp-plan-card ${plan.status === 'archived' ? 'bp-plan-card--archived' : ''}`}>
    <div className="bp-plan-header">
      <div className="bp-plan-name-row">
        <Package size={18} />
        <h3>{plan.name}</h3>
        <Badge color={plan.status === 'active' ? 'green' : plan.status === 'draft' ? 'yellow' : 'gray'}>
          {plan.status}
        </Badge>
        {plan.is_public && <Badge color="blue">Public</Badge>}
        {plan.ai_enabled && (
          <Badge color="purple">
            <Sparkles size={11} style={{verticalAlign:'-2px',marginRight:2}}/>AI
          </Badge>
        )}
      </div>
      <div className="bp-plan-price">{fmtCurrency(plan.base_monthly_fee)}<span>/month</span></div>
    </div>

    <p className="bp-plan-desc">{plan.description || 'No description'}</p>

    <div className="bp-tier-grid">
      {TIERS.map(t => (
        <div key={t.key} className="bp-tier-cell">
          <span className="bp-tier-icon">{t.icon}</span>
          <span className="bp-tier-label">Tier {t.key}</span>
          <span className="bp-tier-included">{fmt(plan[`tier${t.key}_included_calls`])} incl.</span>
          <span className="bp-tier-overage">{fmtCurrency(plan[`tier${t.key}_overage_price`])}/{t.unit}</span>
        </div>
      ))}
    </div>

    <div className="bp-plan-limits">
      <span title="Rate limit"><Zap size={13}/> {plan.rate_limit_per_second}/s</span>
      <span title="Daily quota"><Calendar size={13}/> {plan.daily_quota ? fmt(plan.daily_quota) : '∞'}/day</span>
      <span title="Assigned tenants"><Users size={13}/> {plan.assigned_tenants || 0} tenants</span>
      <span title="Max batch"><Layers size={13}/> Batch {plan.max_batch_size}</span>
    </div>

    <div className="bp-plan-actions">
      <button className="bp-btn bp-btn--sm" onClick={() => onEdit(plan)}><Edit3 size={14}/> Edit</button>
      <button className="bp-btn bp-btn--sm bp-btn--outline" onClick={() => onViewAssignments(plan)}>
        <Tag size={14}/> Assignments
      </button>
      {plan.status === 'active' && (
        <button className="bp-btn bp-btn--sm bp-btn--danger" onClick={() => onArchive(plan.id)}>
          <Archive size={14}/> Archive
        </button>
      )}
    </div>
  </div>
);

/* ===== PLAN FORM MODAL ===== */
const PlanFormModal = ({ plan, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: plan?.name || '',
    slug: plan?.slug || '',
    description: plan?.description || '',
    base_monthly_fee: plan?.base_monthly_fee || 0,
    tier1_included_calls: plan?.tier1_included_calls || 0,
    tier2_included_calls: plan?.tier2_included_calls || 0,
    tier3_included_calls: plan?.tier3_included_calls || 0,
    tier4_included_calls: plan?.tier4_included_calls || 0,
    tier1_overage_price: plan?.tier1_overage_price || 0.50,
    tier2_overage_price: plan?.tier2_overage_price || 0.30,
    tier3_overage_price: plan?.tier3_overage_price || 2.00,
    tier4_overage_price: plan?.tier4_overage_price || 50.00,
    rate_limit_per_second: plan?.rate_limit_per_second || 5,
    daily_quota: plan?.daily_quota || '',
    monthly_quota: plan?.monthly_quota || '',
    burst_limit: plan?.burst_limit || 10,
    max_batch_size: plan?.max_batch_size || 50,
    max_compare_sites: plan?.max_compare_sites || 5,
    max_catchment_radius_km: plan?.max_catchment_radius_km || 1.0,
    is_public: plan?.is_public || false,
    status: plan?.status || 'active',
    // AI add-on (Tier 6 / V135)
    ai_enabled: plan?.ai_enabled || false,
    ai_included_chat_turns: plan?.ai_included_chat_turns || 0,
    ai_included_prompt_tokens: plan?.ai_included_prompt_tokens || 0,
    ai_included_completion_tokens: plan?.ai_included_completion_tokens || 0,
    ai_included_moments_units: plan?.ai_included_moments_units || 0,
    ai_overage_per_chat_turn: plan?.ai_overage_per_chat_turn || 0,
    ai_overage_per_1k_prompt_tokens: plan?.ai_overage_per_1k_prompt_tokens || 0,
    ai_overage_per_1k_completion_tokens: plan?.ai_overage_per_1k_completion_tokens || 0,
    ai_overage_per_moments_unit: plan?.ai_overage_per_moments_unit || 0,
    ai_max_sessions_per_day: plan?.ai_max_sessions_per_day || '',
    ai_max_chat_turns_per_session: plan?.ai_max_chat_turns_per_session || 25,
    ai_default_model: plan?.ai_default_model || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'name' && !plan) {
      setForm(prev => ({ ...prev, slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-') }));
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Plan name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        daily_quota: form.daily_quota || null,
        monthly_quota: form.monthly_quota || null,
        ai_max_sessions_per_day: form.ai_max_sessions_per_day || null,
        ai_default_model: form.ai_default_model || null,
      };
      if (plan?.id) {
        await momentsPlanService.updatePlan(plan.id, payload);
      } else {
        await momentsPlanService.createPlan(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bp-modal-overlay" onClick={onClose}>
      <div className="bp-modal" onClick={e => e.stopPropagation()}>
        <div className="bp-modal-header">
          <h2>{plan?.id ? 'Edit Plan' : 'Create New Plan'}</h2>
          <button className="bp-modal-close" onClick={onClose}><X size={20}/></button>
        </div>

        <div className="bp-modal-body">
          {error && <div className="bp-error">{error}</div>}

          <div className="bp-form-section">
            <h3>Identity</h3>
            <div className="bp-form-row">
              <label>Name<input type="text" value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="e.g. Enterprise Premium"/></label>
              <label>Slug<input type="text" value={form.slug} onChange={e => handleChange('slug', e.target.value)} placeholder="auto-generated" disabled={!!plan?.id}/></label>
            </div>
            <label>Description<textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={2}/></label>
            <div className="bp-form-row">
              <label>Base Monthly Fee (₹)<input type="number" min="0" step="0.01" value={form.base_monthly_fee} onChange={e => handleChange('base_monthly_fee', parseFloat(e.target.value) || 0)}/></label>
              <label className="bp-check-label"><input type="checkbox" checked={form.is_public} onChange={e => handleChange('is_public', e.target.checked)}/> Public (self-service)</label>
              <label>Status
                <select value={form.status} onChange={e => handleChange('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </label>
            </div>
          </div>

          <div className="bp-form-section">
            <h3>Tier Pricing</h3>
            <div className="bp-tier-form-grid">
              <div className="bp-tier-form-header">
                <span>Tier</span><span>Included Calls</span><span>Overage Price (₹)</span>
              </div>
              {TIERS.map(t => (
                <div key={t.key} className="bp-tier-form-row">
                  <span className="bp-tier-form-label">{t.icon} {t.name} <small>(per {t.unit})</small></span>
                  <input type="number" min="0" value={form[`tier${t.key}_included_calls`]}
                    onChange={e => handleChange(`tier${t.key}_included_calls`, parseInt(e.target.value) || 0)}/>
                  <input type="number" min="0" step="0.01" value={form[`tier${t.key}_overage_price`]}
                    onChange={e => handleChange(`tier${t.key}_overage_price`, parseFloat(e.target.value) || 0)}/>
                </div>
              ))}
            </div>
          </div>

          <div className="bp-form-section">
            <h3>Rate Limits & Features</h3>
            <div className="bp-form-row bp-form-row--4">
              <label>Rate/sec<input type="number" min="1" value={form.rate_limit_per_second} onChange={e => handleChange('rate_limit_per_second', parseInt(e.target.value) || 5)}/></label>
              <label>Daily Quota<input type="number" min="0" value={form.daily_quota} onChange={e => handleChange('daily_quota', parseInt(e.target.value) || '')} placeholder="Unlimited"/></label>
              <label>Monthly Quota<input type="number" min="0" value={form.monthly_quota} onChange={e => handleChange('monthly_quota', parseInt(e.target.value) || '')} placeholder="Unlimited"/></label>
              <label>Burst<input type="number" min="1" value={form.burst_limit} onChange={e => handleChange('burst_limit', parseInt(e.target.value) || 10)}/></label>
            </div>
            <div className="bp-form-row">
              <label>Max Batch Size<input type="number" min="1" value={form.max_batch_size} onChange={e => handleChange('max_batch_size', parseInt(e.target.value) || 50)}/></label>
              <label>Max Compare Sites<input type="number" min="1" value={form.max_compare_sites} onChange={e => handleChange('max_compare_sites', parseInt(e.target.value) || 5)}/></label>
              <label>Max Catchment Radius (km)<input type="number" min="0.1" step="0.1" value={form.max_catchment_radius_km} onChange={e => handleChange('max_catchment_radius_km', parseFloat(e.target.value) || 1)}/></label>
            </div>
          </div>

          {/* AI add-on (Tier 6 / V135) */}
          <div className="bp-form-section">
            <h3><Sparkles size={16} style={{verticalAlign:'-2px',marginRight:6}}/>AI Add-on (Tier 6)</h3>
            <label className="bp-check-label">
              <input type="checkbox" checked={form.ai_enabled} onChange={e => handleChange('ai_enabled', e.target.checked)}/>
              Enable Moments AI Chat for this plan
            </label>
            {form.ai_enabled && (
              <>
                <div className="bp-form-row bp-form-row--4">
                  <label>Included chat turns / mo<input type="number" min="0" value={form.ai_included_chat_turns} onChange={e => handleChange('ai_included_chat_turns', parseInt(e.target.value) || 0)}/></label>
                  <label>Included prompt tokens / mo<input type="number" min="0" value={form.ai_included_prompt_tokens} onChange={e => handleChange('ai_included_prompt_tokens', parseInt(e.target.value) || 0)}/></label>
                  <label>Included completion tokens / mo<input type="number" min="0" value={form.ai_included_completion_tokens} onChange={e => handleChange('ai_included_completion_tokens', parseInt(e.target.value) || 0)}/></label>
                  <label>Included AI Moments units / mo<input type="number" min="0" value={form.ai_included_moments_units} onChange={e => handleChange('ai_included_moments_units', parseInt(e.target.value) || 0)}/></label>
                </div>
                <div className="bp-form-row bp-form-row--4">
                  <label>₹ / chat turn (overage)<input type="number" min="0" step="0.01" value={form.ai_overage_per_chat_turn} onChange={e => handleChange('ai_overage_per_chat_turn', parseFloat(e.target.value) || 0)}/></label>
                  <label>₹ / 1k prompt tokens<input type="number" min="0" step="0.01" value={form.ai_overage_per_1k_prompt_tokens} onChange={e => handleChange('ai_overage_per_1k_prompt_tokens', parseFloat(e.target.value) || 0)}/></label>
                  <label>₹ / 1k completion tokens<input type="number" min="0" step="0.01" value={form.ai_overage_per_1k_completion_tokens} onChange={e => handleChange('ai_overage_per_1k_completion_tokens', parseFloat(e.target.value) || 0)}/></label>
                  <label>₹ / AI Moments unit<input type="number" min="0" step="0.01" value={form.ai_overage_per_moments_unit} onChange={e => handleChange('ai_overage_per_moments_unit', parseFloat(e.target.value) || 0)}/></label>
                </div>
                <div className="bp-form-row">
                  <label>Max AI sessions / day<input type="number" min="0" value={form.ai_max_sessions_per_day} onChange={e => handleChange('ai_max_sessions_per_day', parseInt(e.target.value) || '')} placeholder="Unlimited"/></label>
                  <label>Max chat turns / session<input type="number" min="1" value={form.ai_max_chat_turns_per_session} onChange={e => handleChange('ai_max_chat_turns_per_session', parseInt(e.target.value) || 25)}/></label>
                  <label>Default model<input type="text" value={form.ai_default_model} onChange={e => handleChange('ai_default_model', e.target.value)} placeholder="e.g. gpt-4.1-mini"/></label>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bp-modal-footer">
          <button className="bp-btn" onClick={onClose}>Cancel</button>
          <button className="bp-btn bp-btn--primary" onClick={handleSave} disabled={saving}>
            <Save size={14}/> {saving ? 'Saving…' : plan?.id ? 'Update Plan' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== ASSIGNMENT MODAL ===== */
const AssignmentModal = ({ plan, onClose, onSaved }) => {
  const [tenants, setTenants] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [notes, setNotes] = useState('');
  const [overrides, setOverrides] = useState({});
  const [showOverrides, setShowOverrides] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantRes, assignRes] = await Promise.all([
        tenantService.listTenants(),
        momentsPlanService.listAssignments({ plan_id: plan.id }),
      ]);
      setTenants(tenantRes.data?.tenants || tenantRes.data || []);
      setAssignments(assignRes.data?.assignments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [plan.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssign = async () => {
    if (!selectedTenant) return;
    setAssigning(true);
    setError('');
    try {
      await momentsPlanService.assignPlan({
        plan_id: plan.id,
        tenant_id: selectedTenant,
        overrides: showOverrides ? overrides : {},
        notes: notes || undefined,
      });
      setSelectedTenant('');
      setNotes('');
      setOverrides({});
      setShowOverrides(false);
      await loadData();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleCancel = async (assignmentId) => {
    try {
      await momentsPlanService.cancelAssignment(assignmentId);
      await loadData();
      onSaved();
    } catch (err) {
      setError(err.message);
    }
  };

  const assignedTenantIds = new Set(assignments.filter(a => a.status === 'active').map(a => a.tenant_id));
  const availableTenants = tenants.filter(t => !assignedTenantIds.has(t.id));

  return (
    <div className="bp-modal-overlay" onClick={onClose}>
      <div className="bp-modal bp-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="bp-modal-header">
          <h2><Tag size={18}/> Assignments — {plan.name}</h2>
          <button className="bp-modal-close" onClick={onClose}><X size={20}/></button>
        </div>

        <div className="bp-modal-body">
          {error && <div className="bp-error">{error}</div>}

          {/* Assign new tenant */}
          <div className="bp-form-section">
            <h3>Assign to Tenant</h3>
            <div className="bp-form-row">
              <label>Tenant
                <select value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}>
                  <option value="">Select tenant…</option>
                  {availableTenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
              <label>Notes<input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes"/></label>
            </div>
            <label className="bp-check-label">
              <input type="checkbox" checked={showOverrides} onChange={e => setShowOverrides(e.target.checked)}/>
              Custom tier overrides for this tenant
            </label>
            {showOverrides && (
              <div className="bp-tier-form-grid">
                <div className="bp-tier-form-header"><span>Tier</span><span>Included Override</span><span>Overage Override (₹)</span></div>
                {TIERS.map(t => (
                  <div key={t.key} className="bp-tier-form-row">
                    <span className="bp-tier-form-label">{t.icon} {t.name}</span>
                    <input type="number" min="0" value={overrides[`tier${t.key}_included`] || ''}
                      onChange={e => setOverrides(p => ({ ...p, [`tier${t.key}_included`]: parseInt(e.target.value) || undefined }))}
                      placeholder={`Plan: ${fmt(plan[`tier${t.key}_included_calls`])}`}/>
                    <input type="number" min="0" step="0.01" value={overrides[`tier${t.key}_overage`] || ''}
                      onChange={e => setOverrides(p => ({ ...p, [`tier${t.key}_overage`]: parseFloat(e.target.value) || undefined }))}
                      placeholder={`Plan: ${plan[`tier${t.key}_overage_price`]}`}/>
                  </div>
                ))}
              </div>
            )}
            {showOverrides && (
              <div className="bp-form-section" style={{marginTop:'0.5rem',background:'#f8fafc',padding:'0.75rem',borderRadius:6}}>
                <h4 style={{margin:'0 0 0.5rem',fontSize:'0.85rem'}}>
                  <Sparkles size={12} style={{verticalAlign:'-2px',marginRight:4}}/>
                  AI Add-on Override (Tier 6)
                </h4>
                <label className="bp-check-label" style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                  <input
                    type="checkbox"
                    checked={overrides.ai_enabled === true}
                    ref={el => { if (el) el.indeterminate = overrides.ai_enabled == null; }}
                    onChange={e => setOverrides(p => ({ ...p, ai_enabled: e.target.checked }))}
                  />
                  Enable AI for this tenant
                  <small style={{color:'#64748b'}}>
                    (plan default: <strong>{plan.ai_enabled ? 'on' : 'off'}</strong>; leave unchecked to inherit)
                  </small>
                </label>
                {(overrides.ai_enabled || (overrides.ai_enabled == null && plan.ai_enabled)) && (
                  <div className="bp-form-row bp-form-row--4" style={{marginTop:'0.5rem'}}>
                    <label>Included chat turns / mo
                      <input type="number" min="0" value={overrides.ai_included_chat_turns || ''}
                        placeholder={`Plan: ${fmt(plan.ai_included_chat_turns)}`}
                        onChange={e => setOverrides(p => ({ ...p, ai_included_chat_turns: parseInt(e.target.value) || undefined }))}/>
                    </label>
                    <label>₹ / chat turn over
                      <input type="number" min="0" step="0.01" value={overrides.ai_overage_per_chat_turn || ''}
                        placeholder={`Plan: ${plan.ai_overage_per_chat_turn}`}
                        onChange={e => setOverrides(p => ({ ...p, ai_overage_per_chat_turn: parseFloat(e.target.value) || undefined }))}/>
                    </label>
                    <label>Max sessions / day
                      <input type="number" min="0" value={overrides.ai_max_sessions_per_day || ''}
                        placeholder={`Plan: ${plan.ai_max_sessions_per_day || 'Unlimited'}`}
                        onChange={e => setOverrides(p => ({ ...p, ai_max_sessions_per_day: parseInt(e.target.value) || undefined }))}/>
                    </label>
                    <label>Default model
                      <input type="text" value={overrides.ai_default_model || ''}
                        placeholder={plan.ai_default_model || 'inherit'}
                        onChange={e => setOverrides(p => ({ ...p, ai_default_model: e.target.value || undefined }))}/>
                    </label>
                  </div>
                )}
              </div>
            )}
            <button className="bp-btn bp-btn--primary" onClick={handleAssign} disabled={!selectedTenant || assigning}>
              <Tag size={14}/> {assigning ? 'Assigning…' : 'Assign Plan'}
            </button>
          </div>

          {/* Active assignments */}
          <div className="bp-form-section">
            <h3>Active Assignments ({assignments.filter(a => a.status === 'active').length})</h3>
            {loading ? <p className="bp-loading">Loading…</p> : (
              <div className="bp-assignment-list">
                {assignments.length === 0 && <p className="bp-empty">No tenants assigned to this plan yet.</p>}
                {assignments.map(a => (
                  <div key={a.id} className={`bp-assignment-row bp-assignment-row--${a.status}`}>
                    <div className="bp-assignment-info">
                      <strong>{a.tenant_name}</strong>
                      <Badge color={a.status === 'active' ? 'green' : a.status === 'cancelled' ? 'red' : 'gray'}>{a.status}</Badge>
                      <span className="bp-assignment-dates">
                        {fmtDate(a.effective_from)} → {a.effective_until ? fmtDate(a.effective_until) : '∞'}
                      </span>
                      {a.notes && <span className="bp-assignment-notes">{a.notes}</span>}
                    </div>
                    {a.status === 'active' && (
                      <button className="bp-btn bp-btn--sm bp-btn--danger" onClick={() => handleCancel(a.id)}>Cancel</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bp-modal-footer">
          <button className="bp-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ===== CYCLE VIEWER ===== */
const CycleViewer = ({ tenantId, tenantName, onClose }) => {
  const [cycle, setCycle] = useState(null);
  const [plan, setPlan] = useState(null);
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await momentsPlanService.getCycle(tenantId);
        setCycle(res.data?.cycle);
        setPlan(res.data?.plan);
        try {
          const aiRes = await momentsPlanService.getAiCycle(tenantId);
          setAi(aiRes.data || null);
        } catch (_) { /* AI cycle is optional — 404 means no plan/cycle */ }
      } catch (err) {
        setError(err.response?.status === 404 ? 'No active cycle.' : err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  return (
    <div className="bp-modal-overlay" onClick={onClose}>
      <div className="bp-modal" onClick={e => e.stopPropagation()}>
        <div className="bp-modal-header">
          <h2><BarChart3 size={18}/> Usage Cycle — {tenantName}</h2>
          <button className="bp-modal-close" onClick={onClose}><X size={20}/></button>
        </div>
        <div className="bp-modal-body">
          {loading && <p className="bp-loading">Loading…</p>}
          {error && <p className="bp-empty">{error}</p>}
          {plan && cycle && (
            <>
              <div className="bp-cycle-header">
                <Badge color="blue">{plan.name}</Badge>
                <span>{fmtDate(cycle.cycle_start)} — {fmtDate(cycle.cycle_end)}</span>
                <Badge color={cycle.status === 'active' ? 'green' : 'gray'}>{cycle.status}</Badge>
              </div>
              <div className="bp-cycle-grid">
                {TIERS.map(t => {
                  const tierPlan = plan[`tier${t.key}`];
                  if (!tierPlan) return null;
                  const pct = tierPlan.included > 0 ? Math.min(100, (tierPlan.used / tierPlan.included) * 100) : 0;
                  const overBudget = tierPlan.used > tierPlan.included;
                  return (
                    <div key={t.key} className="bp-cycle-tier">
                      <div className="bp-cycle-tier-label">{t.icon} Tier {t.key}: {t.name}</div>
                      <div className="bp-progress-bar">
                        <div className={`bp-progress-fill ${overBudget ? 'bp-progress-fill--over' : ''}`} style={{ width: `${Math.min(pct, 100)}%` }}/>
                      </div>
                      <div className="bp-cycle-tier-stats">
                        <span>{fmt(tierPlan.used)} / {fmt(tierPlan.included)} calls</span>
                        <span>{tierPlan.remaining > 0 ? `${fmt(tierPlan.remaining)} remaining` : `${fmt(tierPlan.used - tierPlan.included)} overage`}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="bp-cycle-totals">
                <div className="bp-cycle-total-item">
                  <span>Total Overage Billed</span>
                  <strong>{fmtCurrency(cycle.total_overage_billed)}</strong>
                </div>
              </div>
              {ai && ai.ai_enabled && ai.cycle && (
                <div className="bp-form-section" style={{marginTop:'1rem'}}>
                  <h3><Sparkles size={14} style={{verticalAlign:'-2px',marginRight:6}}/>AI Usage (Tier 6)</h3>
                  <div className="bp-cycle-grid">
                    <div className="bp-cycle-tier">
                      <div className="bp-cycle-tier-label"><MessageSquare size={12}/> Chat turns</div>
                      <div className="bp-cycle-tier-stats">
                        <span>{fmt(ai.cycle.ai_chat_turns_used)} / {fmt(ai.thresholds?.included_chat_turns)} turns</span>
                        <span>{ai.cycle.ai_sessions_started} sessions</span>
                      </div>
                    </div>
                    <div className="bp-cycle-tier">
                      <div className="bp-cycle-tier-label">Prompt tokens</div>
                      <div className="bp-cycle-tier-stats">
                        <span>{fmt(ai.cycle.ai_prompt_tokens_used)} / {fmt(ai.thresholds?.included_prompt_tokens)}</span>
                      </div>
                    </div>
                    <div className="bp-cycle-tier">
                      <div className="bp-cycle-tier-label">Completion tokens</div>
                      <div className="bp-cycle-tier-stats">
                        <span>{fmt(ai.cycle.ai_completion_tokens_used)} / {fmt(ai.thresholds?.included_completion_tokens)}</span>
                      </div>
                    </div>
                    <div className="bp-cycle-tier">
                      <div className="bp-cycle-tier-label">AI Moments units</div>
                      <div className="bp-cycle-tier-stats">
                        <span>{fmt(ai.cycle.ai_moments_units_used)} / {fmt(ai.thresholds?.included_moments_units)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bp-cycle-totals">
                    <div className="bp-cycle-total-item">
                      <span>AI Overage Billed</span>
                      <strong>{fmtCurrency(ai.cycle.ai_overage_amount)}</strong>
                    </div>
                    <div className="bp-cycle-total-item">
                      <span>Est. Model Cost (USD)</span>
                      <strong>${Number(ai.cycle.ai_estimated_model_cost_usd || 0).toFixed(4)}</strong>
                    </div>
                  </div>
                </div>
              )}
              {ai && !ai.ai_enabled && (
                <div className="bp-empty" style={{marginTop:'1rem'}}>
                  <AlertTriangle size={14}/> AI not enabled on this plan/assignment.
                </div>
              )}
            </>
          )}
        </div>
        <div className="bp-modal-footer">
          <button className="bp-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ===== MAIN PAGE ===== */
const MomentsBillingPlans = () => {
  const currentUser = authClient.getUser();
  const isSuperAdmin = currentUser?.isSuperAdmin;

  const [plans, setPlans] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('plans'); // plans | assignments
  const [editingPlan, setEditingPlan] = useState(null);    // Plan or true (new)
  const [assigningPlan, setAssigningPlan] = useState(null); // Plan for assignment modal
  const [viewingCycle, setViewingCycle] = useState(null);   // { tenantId, tenantName }
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [planRes, assignRes] = await Promise.all([
        momentsPlanService.listPlans(),
        momentsPlanService.listAssignments(),
      ]);
      setPlans(planRes.data?.plans || []);
      setAssignments(assignRes.data?.assignments || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleArchive = async (planId) => {
    if (!window.confirm('Archive this plan? Active assignments must be cancelled first.')) return;
    try {
      await momentsPlanService.archivePlan(planId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bp-page">
        <div className="bp-access-denied">
          <Shield size={48}/>
          <h2>Super Admin Access Required</h2>
          <p>Billing plan management is restricted to super administrators.</p>
        </div>
      </div>
    );
  }

  const filteredPlans = plans.filter(p =>
    !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.slug.includes(searchTerm.toLowerCase())
  );

  const filteredAssignments = assignments.filter(a =>
    !searchTerm || (a.tenant_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      || (a.plan_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bp-page">
      {/* Header */}
      <div className="bp-header">
        <div className="bp-header-left">
          <h1 className="bp-title">
            <DollarSign size={24}/>
            Moments Billing Plans
          </h1>
          <p className="bp-subtitle">Manage tiered billing plans, tenant assignments, and usage cycles</p>
        </div>
        <div className="bp-header-actions">
          <button className="bp-btn bp-btn--outline" onClick={loadData} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'bp-spin' : ''}/> Refresh
          </button>
          <button className="bp-btn bp-btn--primary" onClick={() => setEditingPlan(true)}>
            <Plus size={14}/> New Plan
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bp-stats-row">
        <div className="bp-stat" onClick={() => setView('plans')}>
          <Package size={18} className="bp-stat-icon bp-stat-icon--blue"/>
          <span className="bp-stat-val">{plans.length}</span>
          <span className="bp-stat-lbl">Plans</span>
        </div>
        <div className="bp-stat" onClick={() => setView('plans')}>
          <Globe size={18} className="bp-stat-icon bp-stat-icon--green"/>
          <span className="bp-stat-val">{plans.filter(p => p.status === 'active').length}</span>
          <span className="bp-stat-lbl">Active</span>
        </div>
        <div className="bp-stat" onClick={() => setView('assignments')}>
          <Tag size={18} className="bp-stat-icon bp-stat-icon--purple"/>
          <span className="bp-stat-val">{assignments.filter(a => a.status === 'active').length}</span>
          <span className="bp-stat-lbl">Assignments</span>
        </div>
        <div className="bp-stat">
          <TrendingUp size={18} className="bp-stat-icon bp-stat-icon--amber"/>
          <span className="bp-stat-val">{new Set(assignments.filter(a => a.status === 'active').map(a => a.tenant_id)).size}</span>
          <span className="bp-stat-lbl">Tenants</span>
        </div>
      </div>

      {error && <div className="bp-error">{error}</div>}

      {/* Tabs + Search */}
      <div className="bp-toolbar">
        <div className="bp-tabs">
          <button className={`bp-tab ${view === 'plans' ? 'bp-tab--active' : ''}`} onClick={() => setView('plans')}>
            <Package size={14}/> Plans
          </button>
          <button className={`bp-tab ${view === 'assignments' ? 'bp-tab--active' : ''}`} onClick={() => setView('assignments')}>
            <Tag size={14}/> Tenant Assignments
          </button>
        </div>
        <div className="bp-search">
          <SearchIcon size={14}/>
          <input type="text" placeholder={view === 'plans' ? 'Search plans…' : 'Search tenants…'}
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
        </div>
      </div>

      {/* Plans View */}
      {view === 'plans' && (
        <div className="bp-plan-grid">
          {loading && <p className="bp-loading">Loading plans…</p>}
          {!loading && filteredPlans.length === 0 && <p className="bp-empty">No plans found.</p>}
          {filteredPlans.map(p => (
            <PlanCard key={p.id} plan={p}
              onEdit={() => setEditingPlan(p)}
              onViewAssignments={() => setAssigningPlan(p)}
              onArchive={handleArchive}/>
          ))}
        </div>
      )}

      {/* Assignments View */}
      {view === 'assignments' && (
        <div className="bp-assignments-table-wrap">
          {loading && <p className="bp-loading">Loading assignments…</p>}
          {!loading && filteredAssignments.length === 0 && <p className="bp-empty">No assignments found.</p>}
          {!loading && filteredAssignments.length > 0 && (
            <table className="bp-assignments-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Effective</th>
                  <th>Base Fee</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.map(a => (
                  <tr key={a.id} className={`bp-asgn-row bp-asgn-row--${a.status}`}>
                    <td><strong>{a.tenant_name}</strong></td>
                    <td><Badge color="blue">{a.plan_name}</Badge></td>
                    <td><Badge color={a.status === 'active' ? 'green' : a.status === 'cancelled' ? 'red' : 'gray'}>{a.status}</Badge></td>
                    <td>{fmtDate(a.effective_from)} → {a.effective_until ? fmtDate(a.effective_until) : '∞'}</td>
                    <td>{fmtCurrency(a.custom_base_fee || a.base_monthly_fee)}</td>
                    <td className="bp-notes-cell">{a.notes || '—'}</td>
                    <td>
                      <div className="bp-action-btns">
                        {a.status === 'active' && (
                          <>
                            <button className="bp-btn bp-btn--xs" title="View cycle"
                              onClick={() => setViewingCycle({ tenantId: a.tenant_id, tenantName: a.tenant_name })}>
                              <BarChart3 size={13}/> Cycle
                            </button>
                            <button className="bp-btn bp-btn--xs bp-btn--danger" title="Cancel assignment"
                              onClick={async () => {
                                if (window.confirm(`Cancel ${a.plan_name} for ${a.tenant_name}?`)) {
                                  await momentsPlanService.cancelAssignment(a.id);
                                  loadData();
                                }
                              }}>
                              <X size={13}/>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modals */}
      {editingPlan && (
        <PlanFormModal
          plan={editingPlan === true ? null : editingPlan}
          onClose={() => setEditingPlan(null)}
          onSaved={() => { setEditingPlan(null); loadData(); }}/>
      )}
      {assigningPlan && (
        <AssignmentModal
          plan={assigningPlan}
          onClose={() => setAssigningPlan(null)}
          onSaved={loadData}/>
      )}
      {viewingCycle && (
        <CycleViewer
          tenantId={viewingCycle.tenantId}
          tenantName={viewingCycle.tenantName}
          onClose={() => setViewingCycle(null)}/>
      )}
    </div>
  );
};

export default MomentsBillingPlans;
